import { PrimaryButton, SecondarySideBar, SquareIcon } from "@kalyzee/kalyzee-ui";
import { MeetingChatAdapter, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppStoreDispatch } from "../../contexts/appStore/context";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";
import { isExpert } from "../../helpers/roles";
import { useFeatures } from "../../hooks/features";

export const SideBar = React.memo(({
  chatAdapter,
  highlightRequestsNb,
}: {
  chatAdapter?: MeetingChatAdapter,
  highlightRequestsNb: number,
}) => {
  const { containerAPI } = useMeetingContainerContext();

  const [sessionRole] = useMeetingSelector(containerAPI, "session", session => session?.role);
  const [survey] = useMeetingSelector(containerAPI, "surveys", surveys => surveys?.[0]);

  const features = useFeatures();

  const workBoxMode = useAppStoreSelector(state => state.workBoxMode);
  const workBoxOpen = useAppStoreSelector(state => state.workBoxOpen);
  const dispatch = useAppStoreDispatch();

  const [undreadMessagesNb, setUnreadMessagesNb] = useState(0);

  const surveyWorkBoxCanBeOpened = (
    (sessionRole && isExpert(sessionRole))
    || survey
  );

  useEffect(() => {
    const handle = chatAdapter?.addEventListener("messages", (messages) => {
      setUnreadMessagesNb(messages.filter(m => !m.read).length);
    });
    return () => handle?.flush();
  }, [chatAdapter]);

  useEffect(() => {
    if (workBoxMode === "survey" && workBoxOpen && !surveyWorkBoxCanBeOpened) {
      dispatch("setWorkBoxOpen", false);
    }
  }, [surveyWorkBoxCanBeOpened, workBoxMode, workBoxOpen]);

  const toggleWorkBox = useCallback((mode: "inputs" | "survey" | "highlight" | "chat") => {
    if (workBoxMode === mode && workBoxOpen) {
      dispatch("setWorkBoxOpen", false);
    } else {
      dispatch("setWorkBoxMode", mode);
      dispatch("setWorkBoxOpen", true);
    }
  }, [workBoxMode, workBoxOpen, dispatch]);

  const top: React.ReactNode[] = useMemo(() => ([
    <PrimaryButton key={"workbox-btn-inputs"} onClick={() => toggleWorkBox("inputs")}>
      <SquareIcon.Variants.ComputerScreen
        label={"Affichage"}
        selected={workBoxMode === "inputs" && workBoxOpen}
      />
    </PrimaryButton>,
  ]), [workBoxMode, workBoxOpen, toggleWorkBox]);


  const bottom: React.ReactNode[] = useMemo(() => {
    const _bottom: React.ReactNode[] = [];

    if (features.survey) {
      _bottom.push(
        <PrimaryButton
          key={"workbox-btn-survey"}
          onClick={() => toggleWorkBox("survey")}
          disabled={!survey && !isExpert(sessionRole)}
        >
          <SquareIcon.Variants.Poll
            label="Sondage"
            selected={workBoxMode === "survey" && workBoxOpen}
            notificationCount={survey && !(workBoxMode === "survey" && workBoxOpen) ? 1 : undefined}
          />
        </PrimaryButton>
      );
    }

    if (isExpert(sessionRole) && features.board) {
      _bottom.push(
        <PrimaryButton key={"workbox-btn-highlight"} onClick={() => toggleWorkBox("highlight")}>
          <SquareIcon.Variants.Intervention
            label="Interventions"
            selected={workBoxMode === "highlight" && workBoxOpen}
            notificationCount={highlightRequestsNb > 0 && !(workBoxMode === "highlight" && workBoxOpen) ? highlightRequestsNb : undefined}
          />
        </PrimaryButton>
      );
    }

    // Only show the chat button when a chat is associated with the meeting.
    if (chatAdapter) {
      _bottom.push(
        <PrimaryButton key={"workbox-btn-chat"} onClick={() => toggleWorkBox("chat")}>
          <SquareIcon.Variants.Chat
            label="Chat"
            selected={workBoxMode === "chat" && workBoxOpen}
            notificationCount={undreadMessagesNb ? undreadMessagesNb : undefined}
          />
        </PrimaryButton>
      );
    }

    return _bottom;
  }, [features.survey, features.board, sessionRole, workBoxMode, workBoxOpen, undreadMessagesNb, survey, toggleWorkBox, highlightRequestsNb, chatAdapter]);

  const isMobile = useAppStoreSelector(state => state.isMobile);

  if (!sessionRole) return null; // impossible

  // On mobile, the sidebar is hidden — workbox controls are in the BottomBar
  if (isMobile) return null;

  return (
    <SecondarySideBar
      top={top}
      bottom={bottom}
    />
  );
});
